/* istanbul ignore file */
import { GRPCClient } from 'api/grpc/client';
import { Concept } from 'api/grpc/resources';
import { mapOr } from 'utils/fp';
import { PickPartial } from 'utils/ts';

/**
 * Searches concepts
 */
export const searchConcepts =
  (
    client: GRPCClient,
    {
      userOrOrgId,
      appId,
      workflowId,
      model,
      workflow,
      includeMainAppConcepts,
      fuzzy = true,
      useCases,
      page = 1,
      perPage = 20,
    }: PickPartial<CF.API.PostConceptsSearchesCall, 'name' | 'page' | 'perPage'>,
  ) =>
  async (query: string) => {
    if (!query) {
      return [];
    }

    const call = {
      userOrOrgId,
      appId,
      workflowId,
      model,
      workflow,
      page,
      perPage,
      name: query,
      includeMainAppConcepts,
      fuzzy,
      useCases,
    };

    const response = await client.postConceptsSearches(call);
    return mapOr(response, (r) => r.getConceptsList(), []);
  };

/**
 * Returns first 50 concepts
 */
export const listConcepts =
  (client: GRPCClient, userOrOrgId: string, appId: string, includeMainAppConcepts: boolean, page: number = 1, perPage = 50) =>
  async () => {
    const call = {
      userOrOrgId,
      appId,
      page,
      perPage,
      includeMainAppConcepts,
    };
    const response = await client.listConcepts(call);
    return mapOr(response, (r) => r.getConceptsList(), []);
  };

/**
 * Makes a recurring call to fetch all app concepts
 */
export const listAllConcepts = (client: GRPCClient, userOrOrgId: string, appId: string, includeMainAppConcepts: boolean) => async () => {
  let pageNumber = 1;
  let page = [];
  let allAppConcepts: Concept[] = [];
  do {
    page = await listConcepts(client, userOrOrgId, appId, includeMainAppConcepts, pageNumber)();
    allAppConcepts = allAppConcepts.concat(page);
    pageNumber += 1;
  } while (page.length === 50);

  return allAppConcepts;
};
